import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  Alert,
  Snackbar,
  Grid,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Search,
  Visibility,
  Delete,
  Refresh,
  Category as CategoryIcon,
  Edit,
  PictureAsPdf,
  TrendingUp,
  TrendingDown,
  AttachMoney,
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  subscribeToExpenses,
  subscribeToCategories,
  createExpense,
  deleteExpense,
  getExpenseById,
  createCategory,
  updateCategory,
  archiveCategory,
} from '../firebase/expenseService';
import { debitCashAccount, creditCashAccount } from '../firebase/cashBankService';
import { logActivity, ACTIONS, ENTITY_TYPES } from '../firebase/activityLogService';
import { useBranch } from '../contexts/BranchContext';

const Expenses = ({ currentUser, permissions }) => {
  const isAdmin = permissions?.userType === 'admin';
  const canDelete = isAdmin || permissions?.deleteExpenses === true;
  const { activeBranch } = useBranch();
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all, today, 7days, 30days
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    type: 'expense',
    color: '#ff9800',
  });
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    categoryId: '',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    paymentMethod: 'Cash',
    referenceNo: '',
    notes: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const unsubscribeExpenses = subscribeToExpenses((newData) => {
      setExpenses(prevExpenses => {
        // Intelligent merge - protect critical financial fields
        const existingMap = new Map(prevExpenses.map(e => [e.id, e]));
        
        return newData.map(newExpense => {
          const existing = existingMap.get(newExpense.id);
          
          if (!existing) {
            return newExpense; // New expense, use as-is
          }
          
          // PROTECT IMMUTABLE FINANCIAL FIELDS
          return {
            ...newExpense,
            // Critical fields that should NEVER change
            amount: existing.amount,
            category: existing.category,
            date: existing.date,
            createdAt: existing.createdAt,
            createdBy: existing.createdBy,
          };
        });
      });
      setLoading(false);
    });
    
    const unsubscribeCategories = subscribeToCategories((newData) => {
      setCategories(prevCategories => {
        // Intelligent merge - protect category names
        const existingMap = new Map(prevCategories.map(c => [c.id, c]));
        
        return newData.filter(c => !c.isArchived).map(newCategory => {
          const existing = existingMap.get(newCategory.id);
          
          if (!existing) {
            return newCategory;
          }
          
          // PROTECT IMMUTABLE FIELDS
          return {
            ...newCategory,
            name: existing.name, // Category name should not change randomly
            createdAt: existing.createdAt,
          };
        });
      });
    });
    
    return () => {
      unsubscribeExpenses();
      unsubscribeCategories();
    };
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshing(false);
    showSnackbar('Data refreshed');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const margin = 20;
    let yPos = margin;

    // Header
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('EXPENSES & LOSSES REPORT', margin, yPos);
    yPos += 10;

    // Date and filter info
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, yPos);
    yPos += 5;
    
    const filterText = dateFilter === 'all' ? 'All Time' : 
                      dateFilter === 'today' ? 'Today' :
                      dateFilter === '7days' ? 'Last 7 Days' : 'Last 30 Days';
    doc.text(`Period: ${filterText}`, margin, yPos);
    yPos += 3;

    // Line separator
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;

    // Summary Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('SUMMARY', margin, yPos);
    yPos += 7;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Expenses:`, margin + 5, yPos);
    doc.text(`LKR ${totalExpenses.toLocaleString('en-LK', { minimumFractionDigits: 2 })}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 6;

    doc.text(`Total Losses:`, margin + 5, yPos);
    doc.text(`LKR ${totalLosses.toLocaleString('en-LK', { minimumFractionDigits: 2 })}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 6;

    doc.setLineWidth(0.3);
    doc.line(margin + 5, yPos, pageWidth - margin, yPos);
    yPos += 6;

    doc.setFont('helvetica', 'bold');
    doc.text(`Grand Total:`, margin + 5, yPos);
    doc.text(`LKR ${(totalExpenses + totalLosses).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 10;

    // Detailed Transactions Table
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('DETAILED TRANSACTIONS', margin, yPos);
    yPos += 5;

    // Prepare table data
    const tableData = filteredExpenses.map(expense => [
      expense.date,
      expense.title,
      expense.categoryName,
      expense.categoryType === 'expense' ? 'Expense' : 'Loss',
      `LKR ${parseFloat(expense.amount || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`,
      `LKR ${parseFloat(expense.grandTotal || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Date', 'Title', 'Category', 'Type', 'Amount', 'Total']],
      body: tableData,
      theme: 'plain',
      styles: {
        fontSize: 8,
        cellPadding: 3,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.5,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      margin: { left: margin, right: margin },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, doc.internal.pageSize.height - 10, { align: 'right' });
    }

    // Save
    doc.save(`Expenses_Report_${filterText.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    showSnackbar('PDF exported successfully');
  };

  const filteredExpenses = expenses.filter(expense => {
    // Branch filter
    if (activeBranch && expense.branchId !== activeBranch.id) return false;

    // Text search filter
    const matchesSearch = expense.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.categoryName.toLowerCase().includes(searchTerm.toLowerCase());

    // Date filter
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const expenseDate = new Date(expense.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (dateFilter === 'today') {
        const todayStr = today.toISOString().split('T')[0];
        matchesDate = expense.date === todayStr;
      } else if (dateFilter === '7days') {
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        matchesDate = expenseDate >= sevenDaysAgo;
      } else if (dateFilter === '30days') {
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        matchesDate = expenseDate >= thirtyDaysAgo;
      }
    }
    
    return matchesSearch && matchesDate;
  });

  const handleCreate = async () => {
    if (submitting) return; // Prevent double-click

    if (!formData.title.trim() || !formData.categoryId) {
      showSnackbar('Please fill in required fields', 'error');
      return;
    }

    if (!activeBranch) {
      showSnackbar('Select a branch first', 'error');
      return;
    }

    setSubmitting(true);

    try {
      const category = categories.find(c => c.id === formData.categoryId);

      const expenseData = {
        ...formData,
        categoryName: category.name,
        categoryType: category.type,
        branchId: activeBranch.id,
        recordedBy: currentUser?.uid || '',
        recordedByName: currentUser?.email || '',
      };

      const result = await createExpense(expenseData);
      
      if (result.success) {
        // Cash & Bank: debit account if expense has a cash payment method
        const paymentMethod = formData.paymentMethod;
        if (paymentMethod && paymentMethod !== 'N/A' && parseFloat(formData.amount) > 0) {
          const accountType = paymentMethod === 'Bank' ? 'cash_at_bank'
            : paymentMethod === 'Petty Cash' ? 'petty_cash'
            : 'cash_in_hand';
          debitCashAccount(
            accountType,
            parseFloat(formData.amount) || 0,
            result.id,
            'expense',
            `Expense - ${expenseData.title || expenseData.categoryName}`,
            paymentMethod.toLowerCase(),
            null,
            currentUser
          ); // fire-and-forget
        }
        closeModal();
        showSnackbar('Expense recorded successfully');
        logActivity({
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          userName: currentUser?.displayName || currentUser?.email,
          action: ACTIONS.CREATED_EXPENSE,
          entityType: ENTITY_TYPES.EXPENSE,
          entityId: result.id,
          entityName: expenseData.title || expenseData.categoryName,
          details: { categoryName: expenseData.categoryName, amount: expenseData.amount || 0, paymentMethod: expenseData.paymentMethod }
        });
      } else {
        showSnackbar(result.error || 'Failed to record expense', 'error');
      }
    } catch (error) {
      console.error('Error creating expense:', error);
      showSnackbar('Failed to record expense', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Find expense before deleting to reverse cash entry
      const expenseToDelete = expenses.find(e => e.id === id);
      const result = await deleteExpense(id);
      if (result.success) {
        // Reverse cash ledger entry if expense had a payment method
        if (expenseToDelete) {
          const pm = expenseToDelete.paymentMethod;
          if (pm && pm !== 'N/A' && parseFloat(expenseToDelete.amount || 0) > 0) {
            const accountType = pm === 'Bank' ? 'cash_at_bank'
              : pm === 'Petty Cash' ? 'petty_cash'
              : 'cash_in_hand';
            creditCashAccount(
              accountType,
              parseFloat(expenseToDelete.amount) || 0,
              id,
              'expense_reversal',
              `Expense Reversal - ${expenseToDelete.title || expenseToDelete.categoryName}`,
              pm.toLowerCase(),
              null,
              currentUser
            );
          }
        }
        showSnackbar('Expense deleted successfully');
        logActivity({
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          userName: currentUser?.displayName || currentUser?.email,
          action: ACTIONS.DELETED_EXPENSE,
          entityType: ENTITY_TYPES.EXPENSE,
          entityId: id,
          entityName: expenseToDelete?.title || expenseToDelete?.categoryName || id,
          details: { categoryName: expenseToDelete?.categoryName, amount: expenseToDelete?.amount || 0 }
        });
      } else {
        showSnackbar(result.error || 'Failed to delete expense', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to delete expense', 'error');
    } finally {
      setSubmitting(false);
      setDeleteConfirm(null);
    }
  };

  const handleViewDetails = async (expense) => {
    const result = await getExpenseById(expense.id);
    if (result.success) {
      setSelectedExpense(result.data);
      setShowDetailModal(true);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({
      title: '',
      categoryId: '',
      date: new Date().toISOString().split('T')[0],
      amount: '',
      paymentMethod: 'Cash',
      referenceNo: '',
      notes: '',
    });
  };

  // Category Management Functions
  const handleAddCategory = async () => {
    if (!categoryFormData.name.trim()) {
      showSnackbar('Please enter a category name', 'error');
      return;
    }

    if (editingCategory) {
      const result = await updateCategory(editingCategory.id, categoryFormData);
      if (result.success) {
        showSnackbar('Category updated successfully');
        closeCategoryModal();
      } else {
        showSnackbar(result.error || 'Failed to update category', 'error');
      }
    } else {
      const result = await createCategory(categoryFormData);
      if (result.success) {
        showSnackbar('Category added successfully');
        closeCategoryModal();
      } else {
        showSnackbar(result.error || 'Failed to add category', 'error');
      }
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      type: category.type,
      color: category.color,
    });
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to archive this category?')) {
      const result = await archiveCategory(id);
      if (result.success) {
        showSnackbar('Category archived successfully');
      } else {
        showSnackbar(result.error || 'Failed to archive category', 'error');
      }
    }
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setEditingCategory(null);
    setCategoryFormData({
      name: '',
      type: 'expense',
      color: '#ff9800',
    });
  };

  // Calculate summary stats (scoped to the active branch via filteredExpenses' branch filter)
  const branchExpenses = activeBranch ? expenses.filter(e => e.branchId === activeBranch.id) : expenses;

  const totalExpenses = branchExpenses
    .filter(e => e.categoryType === 'expense')
    .reduce((sum, e) => sum + (parseFloat(e.grandTotal) || 0), 0);

  const totalLosses = branchExpenses
    .filter(e => e.categoryType === 'loss')
    .reduce((sum, e) => sum + (parseFloat(e.grandTotal) || 0), 0);

  const thisMonth = new Date().toISOString().slice(0, 7);
  const thisMonthExpenses = branchExpenses
    .filter(e => e.date.startsWith(thisMonth))
    .reduce((sum, e) => sum + (parseFloat(e.grandTotal) || 0), 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Expenses & Losses
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track business expenses, stock losses, and operational costs
        </Typography>
      </Box>

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', p: 3 }}>
              <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                <AttachMoney sx={{ fontSize: 32, color: '#ffffff' }} />
              </Box>
              <Typography variant="caption" fontWeight={600} sx={{ color: '#ffffff', mb: 'auto' }}>
                Total Expenses
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#ffffff', mt: 2 }}>
                {totalExpenses.toLocaleString('en-LK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </Typography>
              <Typography variant="caption" sx={{ color: '#ffffff', mt: 1 }}>
                LKR
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', p: 3 }}>
              <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                <TrendingDown sx={{ fontSize: 32, color: '#ffffff' }} />
              </Box>
              <Typography variant="caption" fontWeight={600} sx={{ color: '#ffffff', mb: 'auto' }}>
                Total Losses
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#ffffff', mt: 2 }}>
                {totalLosses.toLocaleString('en-LK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </Typography>
              <Typography variant="caption" sx={{ color: '#ffffff', mt: 1 }}>
                LKR
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', p: 3 }}>
              <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                <TrendingUp sx={{ fontSize: 32, color: '#ffffff' }} />
              </Box>
              <Typography variant="caption" fontWeight={600} sx={{ color: '#ffffff', mb: 'auto' }}>
                This Month
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#ffffff', mt: 2 }}>
                {thisMonthExpenses.toLocaleString('en-LK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </Typography>
              <Typography variant="caption" sx={{ color: '#ffffff', mt: 1 }}>
                LKR
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              All Expenses & Losses
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdf />}
                onClick={exportToPDF}
                color="primary"
              >
                Export PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={<CategoryIcon />}
                onClick={() => setShowCategoryModal(true)}
                color="primary"
              >
                Manage Categories
              </Button>
              <Button
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={20} /> : <Refresh />}
                onClick={handleRefresh}
                disabled={refreshing}
                color="primary"
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setShowModal(true)}
                color="primary"
              >
                Add Expense
              </Button>
            </Box>
          </Box>

          {/* Date Filter Chips */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            <Chip
              label="All Time"
              onClick={() => setDateFilter('all')}
              color={dateFilter === 'all' ? 'primary' : 'default'}
              variant={dateFilter === 'all' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Today"
              onClick={() => setDateFilter('today')}
              color={dateFilter === 'today' ? 'primary' : 'default'}
              variant={dateFilter === 'today' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Last 7 Days"
              onClick={() => setDateFilter('7days')}
              color={dateFilter === '7days' ? 'primary' : 'default'}
              variant={dateFilter === '7days' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Last 30 Days"
              onClick={() => setDateFilter('30days')}
              color={dateFilter === '30days' ? 'primary' : 'default'}
              variant={dateFilter === '30days' ? 'filled' : 'outlined'}
            />
          </Box>

          <TextField
            fullWidth
            placeholder="Search by title or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Title</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell align="right"><strong>Amount (LKR)</strong></TableCell>
                  <TableCell><strong>Payment</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredExpenses.map((expense) => (
                  <TableRow 
                    key={expense.id} 
                    hover
                    sx={{
                      borderLeft: `4px solid ${expense.categoryType === 'loss' ? '#f44336' : '#ff9800'}`,
                    }}
                  >
                    <TableCell>
                      {(() => {
                        const [year, month, day] = expense.date.split('-');
                        const yearMonth = `${year}-${month}`;
                        const today = new Date().toISOString().split('T')[0];
                        const todayYearMonth = today.slice(0, 7);
                        const isSameMonth = yearMonth === todayYearMonth;
                        
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isSameMonth ? (
                              <>
                                <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.6 }}>
                                  {yearMonth}
                                </Typography>
                                <Typography variant="body2" fontWeight={700} color="text.secondary">
                                  {day}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="body2">
                                {expense.date}
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                        {expense.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={expense.categoryName}
                        size="small"
                        sx={{
                          backgroundColor: '#fff3e0',
                          color: '#e65100',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={expense.categoryType === 'expense' ? 'Expense' : 'Loss'}
                        size="small"
                        color={expense.categoryType === 'expense' ? 'warning' : 'error'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {(expense.grandTotal || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </TableCell>
                    <TableCell>{expense.paymentMethod}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewDetails(expense)} color="primary">
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canDelete && (
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleteConfirm(expense)} color="error">
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredExpenses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No expenses found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add Expense Modal */}
      <Dialog open={showModal} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle>Add New Expense / Loss</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Expense Title"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.categoryId}
                    label="Category"
                    onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: category.color,
                            }}
                          />
                          {category.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Amount (LKR)"
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={formData.paymentMethod}
                    label="Payment Method"
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                  >
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Bank">Bank Transfer</MenuItem>
                    <MenuItem value="Card">Card</MenuItem>
                    <MenuItem value="Petty Cash">Petty Cash</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Reference No"
                  value={formData.referenceNo}
                  onChange={(e) => setFormData({ ...formData, referenceNo: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeModal} disabled={submitting}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreate} 
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
            color="primary"
          >
            {submitting ? 'Recording...' : 'Record Expense'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onClose={() => setShowDetailModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Expense Details</DialogTitle>
        <DialogContent>
          {selectedExpense && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Title</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedExpense.title}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Category</Typography>
                  <Chip label={selectedExpense.categoryName} size="small" />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Date</Typography>
                  <Typography variant="body1">{selectedExpense.date}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Amount</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    LKR {(selectedExpense.amount || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                  <Typography variant="body1">{selectedExpense.paymentMethod}</Typography>
                </Grid>
                {selectedExpense.referenceNo && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Reference No</Typography>
                    <Typography variant="body1">{selectedExpense.referenceNo}</Typography>
                  </Grid>
                )}
                {selectedExpense.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Notes</Typography>
                    <Typography variant="body1">{selectedExpense.notes}</Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Recorded By</Typography>
                  <Typography variant="body1">{selectedExpense.recordedByName}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Grand Total</Typography>
                  <Typography variant="h6" fontWeight={700} color="primary">
                    LKR {(selectedExpense.grandTotal || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={showCategoryModal} onClose={closeCategoryModal} maxWidth="md" fullWidth>
        <DialogTitle>Manage Expense Categories</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Category Name"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  placeholder="e.g., Utilities, Rent, Salaries"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={categoryFormData.type}
                    label="Type"
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, type: e.target.value })}
                  >
                    <MenuItem value="expense">Expense</MenuItem>
                    <MenuItem value="loss">Loss</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Color"
                  type="color"
                  value={categoryFormData.color}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, color: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleAddCategory}
                  fullWidth
                >
                  {editingCategory ? 'Update Category' : 'Add Category'}
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Existing Categories
            </Typography>

            {categories.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {categories.map((category) => (
                  <Paper
                    key={category.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderLeft: `4px solid ${category.color}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: category.color,
                        }}
                      />
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          {category.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Chip
                            label={category.type === 'expense' ? 'Expense' : 'Loss'}
                            size="small"
                            color={category.type === 'expense' ? 'warning' : 'error'}
                          />
                        </Box>
                      </Box>
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditCategory(category)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteCategory(category.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              <Alert severity="info">
                No categories yet. Add your first category above.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCategoryModal}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Expense?</DialogTitle>
        <DialogContent>
          <Typography>Delete this expense record? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm?.id)}
            disabled={submitting}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Expenses;
